import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { Pencil, Plus, Search, Trash, SearchX, CircleCheck, CircleX, Package } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { ScaleLoader } from "react-spinners";
import { Link } from "@/router";
import { useAdminGetCompetitions, useAdminDeleteCompetition } from "@/services/competitionService";
import { objectToQueryString } from "@/utils/objectToQueryString";
import { queryStringToObject } from "@/utils/queryStringToObject";

const initialState = {
    errorMessage: "",
    isLoading: false,
    filters: {
        id: "",
        code: "",
        name: "",
        active: "",
        default: "",
    },
    competitions: null,
};

const ACTIONS = {
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",
    SET_FILTER: "setFilter",
    SET_FILTERS: "setFilters",
    SET_COMPETITIONS: "setCompetitions",
};

const reducer = (state, action) => {
    switch (action.type) {
        case "setErrorMessage":
        return { ...state, errorMessage: action.payload };
        case "setIsLoading":
        return { ...state, isLoading: action.payload };
        case "setFilter":
        return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } };
        case "setFilters":
        return { ...state, filters: { ...state.filters, ...action.payload } };
        case "setCompetitions":
        return { ...state, competitions: action.payload };
        default:
        console.error("Unknown action.type " + action.type);
        throw new Error();
    }
};

export default function Index() {
    const [, setPageTitle] = useOutletContext();
    const [state, dispatch] = useReducer(reducer, initialState);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const adminGetCompetitions = useAdminGetCompetitions();
    const adminDeleteCompetition = useAdminDeleteCompetition();
    


    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Gestion des compétitions");

        return () => {
            setPageTitle("Titre")
        }
    }, [setPageTitle]);



    /* On récupère les filtres de recherche si ils sont présents dans l'URL */
    useEffect(() => {
        if (searchParams) {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: queryStringToObject(searchParams.toString()) });
        }
    }, [searchParams]);



    /* On met à jour l'URL quand les filtres changent */
    useEffect(() => {
        if(JSON.stringify(state.filters) !== JSON.stringify(searchParams)) {
            setSearchParams(objectToQueryString(state.filters));
        }
    }, [state.filters, setSearchParams, searchParams]);



    /* Handler de la recherche */
    const searchHandler = async () => {
        const filters = state.filters;
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            const competitions = await adminGetCompetitions(filters);
            dispatch({ type: ACTIONS.SET_COMPETITIONS, payload: competitions });
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        } catch (error) {
            console.error("admin/competitions/index::searchHandler", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    /* Gestion du bouton de reset des filtres */
    const clearFiltersHandler = () => {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: initialState.filters });
    }



    /* Handler de la suppression d'une compétition */
    const deleteCompetitionHandler = async (competition) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            await adminDeleteCompetition(competition.id);
            searchHandler();
        } catch (error) {
            console.error("admin/competitions/index::deleteCompetitionHandler::error", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };





    return (
        <>
            {/* Filtres de recherche */}

            <h2 className="text-secondary text-lg flex-none content-center">Filtres de recherche</h2>
            <div className="flex flex-wrap w-full justify-start gap-4">
                <div className="flex flex-col justify-between h-12">
                    <label className="text-primary text-sm" htmlFor="searchID">
                        ID
                    </label>
                    <input
                        type="text"
                        id="searchID"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="ID"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "id", payload: e.target.value })}
                        value={state.filters.id}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchCode">
                        Code
                    </label>
                    <input
                        type="text"
                        id="searchCode"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Code"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "code", payload: e.target.value })}
                        value={state.filters.code}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchName">
                        Nom
                    </label>
                    <input
                        type="text"
                        id="searchName"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Nom"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "name", payload: e.target.value })}
                        value={state.filters.name}
                    />
                </div>
                {/* <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchActive">
                        Active
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchActive" id="searchActive"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "active", payload: e.target.value })}
                        value={state.filters.active}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchDefault">
                        Défaut
                    </label>
                    <select className="border border-gray-300 rounded-sm py-0 text-sm"
                        name="searchDefault" id="searchDefault"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "default", payload: e.target.value })}
                        value={state.filters.default}
                    >
                        <option value="">-</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                    </select>
                </div> */}
                <div className="flex flex-col justify-end">
                    <button 
                        id="searchButton" name="searchButton"
                        className="pb-1" onClick={() => searchHandler()}
                        title="Rechercher">
                        <Search size={32} className="text-primary hover:text-blue-600" />
                    </button>
                </div>
                <div className="flex flex-col justify-end">
                    <button 
                        id="clearFiltersButton" name="clearFiltersButton"
                        className="pb-1" onClick={() => clearFiltersHandler()}
                        title="Nettoyer la recherche">
                        <SearchX size={20} className="text-primary hover:text-red-600" />
                    </button>
                </div>
            </div>

            {/* Affichage de la liste des compétitions */}

            <div className="flex flex-wrap w-full h-14 justify-between mb-3 ">
                <div className="flex content-center">
                    <h2 className="text-secondary text-lg flex-none content-center">Liste des compétitions</h2>
                </div>
                <div className="flex flex-wrap content-center">
                    {state.errorMessage && (
                        <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
                    )}
                    {state.isLoading ? <ScaleLoader height={20} color={"#696969"} cssOverride={{alignSelf: "center"}} /> : null}
                    <Link
                        to="/admin/competitions/add"
                        state={{ from: location}}
                        className="ml-4 border rounded-lg h-10 w-10 border-secondary border-dashed hover:bg-blue-100 content-center"
                    >
                        <Plus className="mx-auto" />
                    </Link>
                </div>
            </div>
            <table className="table-auto w-full border border-separate rounded-xl border-blue-500 border-spacing-0">
                <thead className="bg-blue-200">
                    <tr className="h-10">
                        <th className="px-4 py-2 text-left rounded-tl-xl">Nom</th>
                        <th className="px-4 py-2 text-center">Code</th>
                        <th className="px-4 py-2 text-center">Active</th>
                        <th className="px-4 py-2 text-center">Défaut</th>
                        <th className="px-4 py-2 text-center">Archivée</th>
                        <th className="px-4 py-2 text-center rounded-tr-xl">Métiers</th>
                    </tr>
                </thead>
                <tbody>
                    {state.competitions?.length > 0 
                        ? ( state.competitions.map((competition, i) => {
                            return (
                                <tr
                                    key={competition.id}
                                    className={` ${i % 2 === 0 && "bg-blue-100"} "hover:bg-sky-200" `} >
                                        <th className={`text-left px-1 flex-auto flex-wrap flex flex-col gap-1 ${i === state.competitions.length - 1 && "rounded-bl-xl"}`}>
                                            <div className="text-secondary text-xs flex items-center gap-2">
                                                <span className="content-center">ID:{competition.id}</span>
                                                <button
                                                    onDoubleClick={() => deleteCompetitionHandler(competition)}
                                                    title="Double-cliquez pour supprimer la compétition"
                                                >
                                                    <Trash size={10} className="text-red-500 hover:text-red-700" />
                                                </button>
                                            </div>
                                            <div className="text-primary text-base flex items-center">
                                                <Link to={"/admin/competitions/" + competition.id} state={{ from: location}}>
                                                    <Pencil size={12} className="flex content-center mr-2" />
                                                </Link>

                                                <span className="flex content-center font-bold">
                                                    {competition.name}
                                                </span>
                                            </div>
                                        </th>
                                        <td className="text-center">
                                            <span>{competition.code}</span>
                                        </td>
                                        <td className="text-center justify-center">
                                            <span className="flex items-center justify-center">
                                                {competition.active ? <CircleCheck size={24} className="text-green-700" /> : <CircleX size={24} className="text-red-700" />}
                                            </span>
                                        </td>
                                        <td className={`text-center justify-center`}>
                                            <span className="flex items-center justify-center">
                                                {competition.default ? <CircleCheck size={24} className="text-green-700" /> : <CircleX size={24} className="text-red-700" />}
                                            </span>
                                        </td>
                                        <td className={`text-center justify-center`}>
                                            <span className="flex items-center justify-center">
                                                {competition.archived ? <Package size={24} className="text-green-700" /> : <Package size={24} className="text-gray-400" />}
                                            </span>
                                        </td>
                                        <td className={`text-center text-secondary text-sm justify-center ${i === state.competitions.length - 1 && "rounded-br-xl"}`}>
                                            <span>{competition.skills.length}</span>
                                        </td>
                                </tr>
                            );
                        })) : state.competitions ? (
                            <tr>
                                <td colSpan={5} className="text-center text-secondary py-3">
                                    Aucune compétition trouvée
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan={5} className="text-center text-secondary py-3">
                                    Lancez la recherche pour afficher les compétitions
                                </td>
                            </tr>
                        )}
                </tbody>
            </table>
        </>
    );
}
